import {useQuery} from '@tanstack/react-query'
import {Clock, History} from 'lucide-react'
import {Card, CardContent, CardHeader, CardTitle} from '@/components/ui/card'
import {Badge, BadgeVariant} from '@/components/ui/badge'
import {IconText} from '@/components/shared/IconText'
import {InlineRow} from '@/components/shared/InlineRow'
import {MutedText} from '@/components/shared/MutedText'
import {Stack} from '@/components/shared/Stack'
import {TimelineItem} from '@/components/shared/TimelineItem'
import {formatTimestamp} from '@/lib/date-utils'
import {useI18n} from '@/i18n'
import {useServices} from '@/services/context'

interface VersionHistoryCardProps {
    ruleId: string
}

const PAGE_SIZE = 10

export function VersionHistoryCard({ruleId}: VersionHistoryCardProps) {
    const services = useServices()
    const i18n = useI18n()

    const {data: page, isLoading, isError} = useQuery({
        queryKey: ['restriction-rule-versions', ruleId, PAGE_SIZE],
        queryFn: () => services.restrictionRules.versions(ruleId, {size: PAGE_SIZE}),
        enabled: !!ruleId,
    })

    const versions = page?.content ?? []

    return (
        <Card data-testid="version-history-card">
            <CardHeader>
                <CardTitle className="text-base" data-testid="card-title-version-history">
                    <IconText icon={History} size="sm">
                        {i18n.restrictionRules.versionHistory}
                    </IconText>
                </CardTitle>
            </CardHeader>
            <CardContent>
                {isLoading ? (
                    <MutedText size="sm" data-testid="version-loading">
                        {i18n.restrictionRules.versionLoading}
                    </MutedText>
                ) : isError ? (
                    <MutedText size="sm" data-testid="version-error">
                        {i18n.restrictionRules.versionLoadFailed}
                    </MutedText>
                ) : versions.length === 0 ? (
                    <MutedText size="sm" data-testid="version-empty">
                        {i18n.restrictionRules.noVersionHistory}
                    </MutedText>
                ) : (
                    <Stack gap="sm">
                        {versions.map((version) => (
                            <TimelineItem key={version.id} data-testid={`version-row-${version.id}`}>
                                <InlineRow gap="md">
                                    <Badge variant="outline" className="text-xs"
                                           data-testid={`version-number-${version.id}`}>
                                        {i18n.common.versionPrefix({version: version.versionNumber})}
                                    </Badge>
                                    {version.current && (
                                        <Badge variant={BadgeVariant.SUCCESS} className="text-xs"
                                               data-testid={`version-current-${version.id}`}>
                                            {i18n.restrictionRules.versionCurrent}
                                        </Badge>
                                    )}
                                </InlineRow>
                                <InlineRow gap="md" className="mt-1">
                                    <IconText icon={Clock} size="sm">
                                        <MutedText size="xs">
                                            {formatTimestamp(version.createdAt)}
                                        </MutedText>
                                    </IconText>
                                    {version.createdBy && (
                                        <MutedText size="xs" data-testid={`version-author-${version.id}`}>
                                            {i18n.restrictionRules.versionByAuthor({name: version.createdBy.displayName})}
                                        </MutedText>
                                    )}
                                </InlineRow>
                            </TimelineItem>
                        ))}
                    </Stack>
                )}
            </CardContent>
        </Card>
    )
}
